import React, { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const NotificationBell = () => {
  const { lang } = useLanguage();
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [supported, setSupported] = useState(true);

  const texts = {
    de: {
      subscribe: 'Benachrichtigungen aktivieren',
      subscribed: 'Benachrichtigungen aktiv',
      unsubscribe: 'Benachrichtigungen deaktivieren',
      unsupported: 'Nicht unterstuetzt',
      denied: 'Benachrichtigungen blockiert',
    },
    en: {
      subscribe: 'Enable notifications',
      subscribed: 'Notifications active',
      unsubscribe: 'Disable notifications',
      unsupported: 'Not supported',
      denied: 'Notifications blocked',
    },
    fr: {
      subscribe: 'Activer les notifications',
      subscribed: 'Notifications actives',
      unsubscribe: 'Desactiver les notifications',
      unsupported: 'Non pris en charge',
      denied: 'Notifications bloquees',
    },
  };
  const tx = texts[lang] || texts.de;

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      return;
    }
    setPermission(Notification.permission);

    // Check existing subscription
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    }).catch(() => {});
  }, []);

  const registerServiceWorker = async () => {
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;
    return registration;
  };

  const handleSubscribe = async () => {
    if (!supported || loading) return;
    setLoading(true);

    try {
      const reg = await registerServiceWorker();

      if (Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        setPermission(result);
        if (result !== 'granted') {
          setLoading(false);
          return;
        }
      } else if (Notification.permission === 'denied') {
        setLoading(false);
        return;
      }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const subJson = subscription.toJSON();
      await fetch(`${API_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        }),
      });

      setSubscribed(true);
    } catch (err) {
      console.error('Push subscription failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        const subJson = subscription.toJSON();
        await fetch(`${API_URL}/api/notifications/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: subJson.endpoint,
            keys: subJson.keys,
          }),
        });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      console.error('Push unsubscribe failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!supported) return null;

  const isDenied = permission === 'denied';

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={subscribed ? handleUnsubscribe : handleSubscribe}
        disabled={loading || isDenied}
        data-testid="notification-bell-btn"
        className={`p-2 rounded-full transition-all ${
          subscribed
            ? 'bg-[#0f2440] text-yellow-300 hover:bg-[#1a3a5c]'
            : isDenied
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-[#0f2440]'
        } ${loading ? 'animate-pulse' : ''}`}
        aria-label={subscribed ? tx.unsubscribe : tx.subscribe}
      >
        {subscribed ? (
          <BellRing className="w-5 h-5" />
        ) : isDenied ? (
          <BellOff className="w-5 h-5" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
      </button>

      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg z-50 pointer-events-none">
          {isDenied ? tx.denied : subscribed ? tx.subscribed : tx.subscribe}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
